import React, { useState, useEffect } from 'react'
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  DatePicker,
  Popconfirm,
  Breadcrumb,
  Tag,
  Card,
  Row,
  Col,
  message,
  Alert,
} from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  HomeOutlined,
  AlertOutlined,
} from '@ant-design/icons'
import type { ColumnsType } from 'antd/es/table'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { consumablesService, Consumable } from '@/renderer/services/consumablesService'
import './ConsumablesList.css'

const CONSUMABLE_STATUSES = [
  { label: 'Available (AVLB)', value: 'AVLB', color: 'green' },
  { label: 'In Use (NUSE)', value: 'NUSE', color: 'blue' },
  { label: 'Low Stock', value: 'LOW_STOCK', color: 'orange' },
  { label: 'Expired', value: 'EXPIRED', color: 'red' },
]

const CONSUMABLE_CATEGORIES = [
  { label: 'Lubricants', value: 'lubricants' },
  { label: 'Seals', value: 'seals' },
  { label: 'Cables & Couplings', value: 'cables' },
  { label: 'Wire rope', value: 'wire_rope' },
  { label: 'Hydraulic Fluid', value: 'hydraulic_fluid' },
  { label: 'Other', value: 'other' },
]

const UNITS_OF_MEASURE = [
  { label: 'Liters', value: 'L' },
  { label: 'Kilograms', value: 'kg' },
  { label: 'Units', value: 'units' },
  { label: 'Boxes', value: 'boxes' },
  { label: 'Gallons', value: 'gal' },
  { label: 'Pounds', value: 'lbs' },
]

const COMPANY_LOCATIONS = [
  { label: 'Neuquén - Operaciones', value: 'Neuquén - Operaciones' },
  { label: 'Neuquén - Base', value: 'Neuquén - Base' },
  { label: 'Comodoro - Operaciones', value: 'Comodoro - Operaciones' },
  { label: 'Comodoro - Base', value: 'Comodoro - Base' },
  { label: 'Buenos Aires - Office', value: 'Buenos Aires - Office' },
]

interface ExtendedConsumable extends Consumable {
  key: string
  stockStatus?: 'low' | 'expired' | 'ok'
}

export function ConsumablesList() {
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [consumables, setConsumables] = useState<ExtendedConsumable[]>([])
  const [loading, setLoading] = useState(false)
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [editingConsumable, setEditingConsumable] = useState<ExtendedConsumable | null>(null)
  const [pagination, setPagination] = useState({ current: 1, pageSize: 50, total: 0 })
  const [filters, setFilters] = useState({ status: '', location: '', category: '' })

  useEffect(() => {
    loadConsumables()
  }, [pagination.current, filters])

  const loadConsumables = async () => {
    setLoading(true)
    try {
      const skip = (pagination.current - 1) * pagination.pageSize
      const response = await consumablesService.listConsumables(skip, pagination.pageSize, filters)
      const consumablesWithKeys: ExtendedConsumable[] = response.items.map((item) => ({
        ...item,
        key: item.id,
        stockStatus: getStockStatus(item),
      }))
      setConsumables(consumablesWithKeys)
      setPagination({ ...pagination, total: response.total })
    } catch (error) {
      message.error('Failed to load consumables')
    } finally {
      setLoading(false)
    }
  }

  const getStockStatus = (item: Consumable): 'low' | 'expired' | 'ok' => {
    if (item.expiration_date && dayjs(item.expiration_date).isBefore(dayjs())) {
      return 'expired'
    }
    if (item.quantity_on_hand <= item.reorder_level) {
      return 'low'
    }
    return 'ok'
  }

  const handleAdd = () => {
    form.resetFields()
    setEditingConsumable(null)
    setIsModalVisible(true)
  }

  const handleEdit = (item: ExtendedConsumable) => {
    form.setFieldsValue({
      ...item,
      expiration_date: item.expiration_date ? dayjs(item.expiration_date) : undefined,
    })
    setEditingConsumable(item)
    setIsModalVisible(true)
  }

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields()
      const dataToSend = {
        ...values,
        expiration_date: values.expiration_date ? values.expiration_date.format('YYYY-MM-DD') : undefined,
      }

      if (editingConsumable) {
        await consumablesService.updateConsumable(editingConsumable.id, dataToSend)
        message.success('Consumable updated successfully')
      } else {
        await consumablesService.createConsumable(dataToSend)
        message.success('Consumable created successfully')
      }
      setIsModalVisible(false)
      setPagination({ ...pagination, current: 1 })
      await loadConsumables()
    } catch (error) {
      console.error('Error saving consumable:', error)
      message.error('Failed to save consumable')
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await consumablesService.deleteConsumable(id)
      message.success('Consumable deleted successfully')
      await loadConsumables()
    } catch (error) {
      console.error('Error deleting consumable:', error)
      message.error('Failed to delete consumable')
    }
  }

  const columns: ColumnsType<ExtendedConsumable> = [
    {
      title: 'Item Name',
      dataIndex: 'item_name',
      key: 'item_name',
      width: 150,
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      width: 120,
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity_on_hand',
      key: 'quantity_on_hand',
      width: 100,
      render: (qty: number, record: ExtendedConsumable) => (
        <span>
          {qty} {record.unit_of_measure}
        </span>
      ),
    },
    {
      title: 'Reorder Level',
      dataIndex: 'reorder_level',
      key: 'reorder_level',
      width: 100,
    },
    {
      title: 'Stock Status',
      dataIndex: 'stockStatus',
      key: 'stockStatus',
      width: 100,
      render: (status: string, record: ExtendedConsumable) => {
        if (record.stockStatus === 'expired') {
          return <Tag color="red">Expired</Tag>
        } else if (record.stockStatus === 'low') {
          return <Tag color="orange">Low Stock</Tag>
        }
        return <Tag color="green">OK</Tag>
      },
    },
    {
      title: 'Expiration',
      dataIndex: 'expiration_date',
      key: 'expiration_date',
      width: 120,
      render: (date: string) => (date ? dayjs(date).format('MMM DD, YYYY') : '-'),
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      width: 140,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(record)} size="small" />
          <Popconfirm
            title="Delete Consumable"
            description="Are you sure you want to delete this consumable?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="text" icon={<DeleteOutlined />} danger size="small" />
          </Popconfirm>
        </Space>
      ),
    },
  ]

  const lowStockCount = consumables.filter((c) => c.stockStatus === 'low').length
  const expiredCount = consumables.filter((c) => c.stockStatus === 'expired').length

  return (
    <div style={{ padding: '24px' }}>
      <Breadcrumb style={{ marginBottom: '16px' }}>
        <Breadcrumb.Item>
          <a onClick={() => navigate('/inventory')}>
            <HomeOutlined />
          </a>
        </Breadcrumb.Item>
        <Breadcrumb.Item>Inventory</Breadcrumb.Item>
        <Breadcrumb.Item>Consumables</Breadcrumb.Item>
      </Breadcrumb>

      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ margin: '0 0 8px 0', fontSize: '24px', fontWeight: 600 }}>Consumables Management</h1>
        <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>
          Manage lubricants, seals, cables, and other consumable items with quantity and expiration tracking
        </p>
      </div>

      {expiredCount > 0 && (
        <Alert
          message={`${expiredCount} consumables are expired`}
          type="error"
          icon={<AlertOutlined />}
          style={{ marginBottom: '16px' }}
          showIcon
        />
      )}

      {lowStockCount > 0 && (
        <Alert
          message={`${lowStockCount} consumables are low on stock`}
          type="warning"
          icon={<AlertOutlined />}
          style={{ marginBottom: '16px' }}
          showIcon
        />
      )}

      <Card style={{ marginBottom: '16px' }}>
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Space wrap>
              <Select
                placeholder="Filter by Status"
                style={{ width: 180 }}
                allowClear
                options={CONSUMABLE_STATUSES}
                onChange={(value) => {
                  setFilters({ ...filters, status: value || '' })
                  setPagination({ ...pagination, current: 1 })
                }}
              />
              <Select
                placeholder="Filter by Location"
                style={{ width: 180 }}
                allowClear
                options={COMPANY_LOCATIONS}
                onChange={(value) => {
                  setFilters({ ...filters, location: value || '' })
                  setPagination({ ...pagination, current: 1 })
                }}
              />
              <Select
                placeholder="Filter by Category"
                style={{ width: 180 }}
                allowClear
                options={CONSUMABLE_CATEGORIES}
                onChange={(value) => {
                  setFilters({ ...filters, category: value || '' })
                  setPagination({ ...pagination, current: 1 })
                }}
              />
            </Space>
          </Col>
          <Col>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
              Add Consumable
            </Button>
          </Col>
        </Row>
      </Card>

      <Table
        columns={columns}
        dataSource={consumables}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          onChange: (page) => setPagination({ ...pagination, current: page }),
        }}
        size="small"
        scroll={{ x: 1200 }}
      />

      <Modal
        title={editingConsumable ? 'Edit Consumable' : 'Add Consumable'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={() => setIsModalVisible(false)}
        width={600}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label="Item Name"
            name="item_name"
            rules={[{ required: true, message: 'Item Name is required' }]}
          >
            <Input placeholder="e.g., Wireline Lubricant, Seal Kit" />
          </Form.Item>

          <Form.Item
            label="Category"
            name="category"
            rules={[{ required: true, message: 'Category is required' }]}
          >
            <Select placeholder="Select category" options={CONSUMABLE_CATEGORIES} />
          </Form.Item>

          <Form.Item
            label="Status"
            name="status"
            rules={[{ required: true, message: 'Status is required' }]}
          >
            <Select placeholder="Select status" options={CONSUMABLE_STATUSES} />
          </Form.Item>

          <Form.Item
            label="Location"
            name="location"
            rules={[{ required: true, message: 'Location is required' }]}
          >
            <Select placeholder="Select location" options={COMPANY_LOCATIONS} />
          </Form.Item>

          <Form.Item
            label="Quantity on Hand"
            name="quantity_on_hand"
            rules={[{ required: true, message: 'Quantity is required' }]}
          >
            <InputNumber placeholder="0" min={0} />
          </Form.Item>

          <Form.Item
            label="Unit of Measure"
            name="unit_of_measure"
            rules={[{ required: true, message: 'Unit is required' }]}
          >
            <Select placeholder="Select unit" options={UNITS_OF_MEASURE} />
          </Form.Item>

          <Form.Item
            label="Reorder Level"
            name="reorder_level"
            rules={[{ required: true, message: 'Reorder level is required' }]}
          >
            <InputNumber placeholder="0" min={0} />
          </Form.Item>

          <Form.Item
            label="Reorder Quantity"
            name="reorder_quantity"
            rules={[{ required: true, message: 'Reorder quantity is required' }]}
          >
            <InputNumber placeholder="0" min={0} />
          </Form.Item>

          <Form.Item label="Supplier" name="supplier">
            <Input placeholder="Supplier name" />
          </Form.Item>

          <Form.Item label="Unit Cost" name="unit_cost">
            <InputNumber placeholder="0.00" min={0} step={0.01} />
          </Form.Item>

          <Form.Item label="Expiration Date" name="expiration_date">
            <DatePicker />
          </Form.Item>

          <Form.Item label="Batch Number" name="batch_number">
            <Input placeholder="Batch/Lot number" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}
